import { Type, TemplateRef } from '@angular/core';

/**
 * enums
 */
import { SharedUtils } from "@shared/utils/shared-utils";
import { IThemeColorVariant } from "@core/enums/theme-colors-variants";
import { ITableResponsiveBreakpoint, TableContentRendererEnum, TableActionMode, ITableActionMode } from "../enums";
/**
 * interfaces
 */
import {
    ITableAction,
    ITableColumnUnion,
    ITableColumnActions,
    ITableSortMeta,
    TableSortMode,
    TableContentRenderer,
    ITableContentRendererInfo, 
    ITableTemplateRenderer,
    ITableComponentRenderer,
    TableContentRendererUnion,
    TableSortOrderType,
} from "../interfaces";

export abstract class TableUtils {

    // ===========================
    //  Table Styles Utilities
    // ===========================

    /**
     * @description Generate the responsive class for the table
     */
    public static generateResponsiveClass(responsive: boolean | ITableResponsiveBreakpoint | ITableResponsiveBreakpoint[]): string | null {

        // if responsive is a boolean, return the class
        if (typeof responsive === 'boolean') {
            return responsive ? 'table-responsive' : null;
        }

        // if responsive is an object, return the class
        if (typeof responsive === 'string') {
            return `table-responsive-${responsive}`;
        }

        // if responsive is an array, return the class
        if (Array.isArray(responsive)) {
            return responsive.map(breakpoint => `table-responsive-${breakpoint}`).join(' ');
        }

        // return an empty string
        return null;
    }

    /**
     * @description Generate the variant class for the table
     */
    public static generateVariantClass(variant: IThemeColorVariant): string | null {
        
        // if variant is not a string or is null, return null
        if (typeof variant !== 'string' || variant == null) return null;

        // generate the variant class
        return SharedUtils.generateVariantClassStyle(variant, 'table');
    }

    // ===========================
    //  ColumnRenderer Utilities
    // ===========================

    /**
     * @description Check if renderer is a string
     */
    public static isStringRenderer<TContext>(renderer: TableContentRenderer<TContext>): renderer is string {

        // if renderer is not an object or is null, return false
        if (typeof renderer === 'object' || renderer == null) return false;
        
        // if renderer is a string, return true
        return typeof renderer.toString() === 'string';
    }

    /**
     * @description Check if renderer is an HTMLElement
     */
    public static isHTMLElementRenderer<TContext>(renderer: TableContentRenderer<TContext>): renderer is HTMLElement {
        return renderer instanceof HTMLElement;
    }

    /**
     * @description Check if renderer is template object (with template property)
     */
    public static isTemplateRenderer<TContext>(renderer: TableContentRenderer<TContext>): renderer is ITableTemplateRenderer<TContext> {
        // if renderer is null, return false
        if (renderer == null) return false;

        return typeof renderer === 'object' && 'template' in renderer && renderer.template instanceof TemplateRef;
    }

    /**
     * @description Check if renderer is component object (with component property)
     */
    public static isComponentRenderer<TContext>(renderer: TableContentRenderer<TContext>): renderer is ITableComponentRenderer {

        // if renderer is null, return false
        if (renderer == null) return false;

        return typeof renderer === 'object' && 'component' in renderer && renderer.component instanceof Type;
    }

    /**
     * @description Check if renderer is a function renderer
     */
    public static isFunctionRenderer<TContext>(renderer: unknown): renderer is (ctx: TContext) => TableContentRenderer<TContext> {
        return typeof renderer === 'function';
    }

    /**
     * @description Get renderer information from a ColumnRenderer or function
     */
    public static getRendererInfo<TContext>(_renderer: TableContentRendererUnion<TContext>, dataContext: TContext): ITableContentRendererInfo<TContext> | null {
        
        let resolvedRenderer = _renderer;

        // Resolve function renderer first (recursive resolution)
        if (this.isFunctionRenderer<TContext>(resolvedRenderer)) {
            resolvedRenderer = resolvedRenderer(dataContext);
        }
        
        if (this.isStringRenderer(resolvedRenderer)) {
            return {
                renderer: resolvedRenderer,
                type: TableContentRendererEnum.String,
            };
        }

        if (this.isHTMLElementRenderer(resolvedRenderer)) {
            return {
                renderer: resolvedRenderer,
                type: TableContentRendererEnum.HTMLElement,
            };
        }

        if (this.isTemplateRenderer<TContext>(resolvedRenderer)) {
            
            // ES6 destructuring
            const { template, context } = resolvedRenderer;

            return {
                renderer: {
                    template,
                    context: { ...context, ...dataContext },
                },
                type: TableContentRendererEnum.Template,
            };
        }

        if (this.isComponentRenderer(resolvedRenderer)) {
            return {
                renderer: resolvedRenderer,
                type: TableContentRendererEnum.Type,
            };
        }

        return null;
    }

    // ===========================
    //  Sorting Utilities
    // ===========================

    /**
     * @description Cycles sort order: null -> asc -> desc -> null
     */
    public static getNextSortOrder(current: TableSortOrderType | null): TableSortOrderType | null {
        if (current == null) return 'asc';
        if (current === 'asc') return 'desc';
        return null;
    }

    /**
     * @description Builds next sort meta for single/multiple sorting.
     * @param currentMeta - Current sort state
     * @param field - Field being sorted
     * @param mode - Sorting mode
     * @param append - When true in multiple mode, keeps existing fields and appends/updates this field
     */
    public static nextSortMeta(
        currentMeta: ITableSortMeta[],
        field: string,
        mode: TableSortMode,
        append: boolean
    ): ITableSortMeta[] {
        if (typeof field !== 'string' || field.trim().length === 0) return currentMeta ?? [];

        const safeMeta = Array.isArray(currentMeta) ? [...currentMeta] : [];
        const index = safeMeta.findIndex(meta => meta.column === field);
        const currentOrder = index >= 0 ? safeMeta[index].order : null;
        const nextOrder = this.getNextSortOrder(currentOrder);

        // single mode always replaces whole state with current field result
        if (mode === 'single') {
            return nextOrder == null ? [] : [{ column: field, order: nextOrder }];
        }

        // multiple mode without append behaves like primary replace
        if (!append) {
            return nextOrder == null ? [] : [{ column: field, order: nextOrder }];
        }

        // multiple + append: remove if cycled to null
        if (nextOrder == null) {
            return safeMeta.filter(meta => meta.column !== field);
        }

        // multiple + append: update existing
        if (index >= 0) {
            safeMeta[index] = {
                column: field,
                order: nextOrder,
            };
            return safeMeta;
        }

        // multiple + append: add new
        return [
            ...safeMeta,
            {
                column: field,
                order: nextOrder,
            }
        ];
    }

    // ===========================
    //  Action Utilities
    // ===========================

    /**
     * @description Check if column is an actions column
     * @param column - The column to check
     * @returns True if column is an actions column
     */
    public static isColumnActions<TContext>(column: ITableColumnUnion<TContext>): column is ITableColumnActions<TContext> {
        return 'actions' in column
    }

    /**
     * @description Check if action is visible (handles both boolean and function)
     * @param action - The action to check
     * @param row - The row data
     * @returns True if action should be visible
     */
    public static isActionVisible<TContext>(action: ITableAction<TContext>, row: any): boolean {
        if (action.visible == null) return true;
        
        if (typeof action.visible === 'function') {
            return action.visible(row);
        }
        
        return action.visible;
    }

    /**
     * @description Check if action is disabled (handles both boolean and function)
     * @param action - The action to check
     * @param row - The row data
     * @returns True if action should be disabled
     */
    public static isActionDisabled<TContext>(action: ITableAction<TContext>, row: any): boolean {
        if (action.disabled == null) return false;
        
        if (typeof action.disabled === 'function') {
            return action.disabled(row);
        }
        
        return action.disabled;
    }

    /**
     * @description Get action mode (uses action.actionMode, defaults to Inline if not specified)
     * @param action - The action to get mode for
     * @returns The action mode
     */
    public static getActionMode<TContext>(action: ITableAction<TContext>): ITableActionMode {
        return action.actionMode ?? TableActionMode.Menu;
    }

    /**
     * @description Execute action command
     * @param action - The action to execute
     * @param row - The row data
     */
    public static executeAction<TContext>(action: ITableAction<TContext>, row: any): void {
        if (action.command instanceof Function) {
            action.command(row, action);
        }
    }

    /**
     * @description Filter actions by their mode
     * @param actions - Array of actions
     * @param row - The row data
     * @param mode - The mode to filter by
     * @returns Filtered actions that match the mode
     */
    public static getActionsByMode<TContext>(
        actions: ITableAction<TContext>[], 
        row: any, 
        mode: ITableActionMode
    ): ITableAction<TContext>[] {
        return actions.filter(action => {
            if (!this.isActionVisible(action, row)) return false;
            
            const actionMode = this.getActionMode(action);
            return actionMode === mode;
        });
    }

}
